import React from 'react'
import { Box, Grid } from '@mui/material'
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { hashHistory } from 'react-router'
import { useStyle } from './Style'
import SkeletonWrapper from '../../../../../containers/NewProductList/components/SkeletonWrapper/SkeletonWrapper'
import { getPartnerGuid, hasAuthourity } from '../../../../../lib/access.es6'
import {
  formatDateLocale,
  getOmMalawiDisplayStatus
} from '../../../../../lib/utils.es6'
import CoverLinkPolicyDetails from './Components/Coverlink/CoverLinkPolicyDetails'
import ReinstatePolicy from './Components/Coverlink/ReinstatePolicy'
import PolicyDetailsFlp from './PolicyDetailsFlp'
import useProductCurrency from '../../../../../hooks/useProductCurrency'
import CancellationRequestDialog from '../../../../OldMutualMalawi/PoliciesProcessing/Components/CancellationRequestDialog'
import {
  PARTNERS_WITH_POLICY_NUMBER_SPECIAL_CHARACTORS,
  PARTNERS_GUID_ENUM,
  TRANSFER_POLICY_ALLOWED_PARTNERS
} from '../../../../../routes/PartnersEnum'

const UGANDA_AMENDMENT_PARTNERS = [
  PARTNERS_GUID_ENUM.OLDMUTUAL_UGANDA_POSTBANK,
  PARTNERS_GUID_ENUM.OLDMUTUAL_UGANDA_ROTARY,
  PARTNERS_GUID_ENUM.OLDMUTUAL_UGANDA_NAMIREMBE,
  PARTNERS_GUID_ENUM.OLDMUTUAL_CENTENARY_BANK,
  PARTNERS_GUID_ENUM.OLDMUTUAL_UGANDA_LIONSCLUB
]

// Partners whose amendment happens inside the embedded policy management
// screen (/admin/amend-policy) rather than by re-creating the quotation
const EMBEDDED_AMENDMENT_PARTNERS = [
  ...UGANDA_AMENDMENT_PARTNERS,
  PARTNERS_GUID_ENUM.SAMBA
]

// Policies can only be amended while they are in one of these statuses
const AMENDMENT_ALLOWED_STATUSES = [
  'Full_Coverage',
  'Un_Confirmed',
  'In_Arrears',
  'Pending',
  'Unpaid'
]

// Whether the Amend action should be available for a given policy
const canAmendPolicy = policy =>
  AMENDMENT_ALLOWED_STATUSES.includes(policy.status)

// Matches the /policies/complex/reinstate eligibility check in
// UpsellProcessor.reinstatePolicy (policy-administration-complex) - showing
// the action outside these statuses would just surface the backend's
// ConflictException instead of a useful UI state.
const REINSTATEMENT_ALLOWED_STATUSES = [
  'Cancelled',
  'Non_Payment',
  'Not_Taken_Up'
]

// Reinstate is currently only exposed for Samba and Access Bank - the
// product's reinstatement config (product-configuration) has to be active
// for the backend call to succeed at all, and that's only been turned on for
// a handful of products so far. Access Bank policies that never took up
// (Not_Taken_Up) or lapsed to Non_Payment can be reinstated once their plan
// has reinstatement enabled.
const REINSTATEMENT_ALLOWED_PARTNERS = [
  PARTNERS_GUID_ENUM.SAMBA,
  PARTNERS_GUID_ENUM.ACCESS_BANK
]

const ITEM_HEIGHT = 48

/**
 * Get the plan name for display
 * For AccessBank, uses primary_plan from quotation.additional_details
 * Replaces underscores with spaces (e.g., "Plan_3" -> "Plan 3")
 */
const getPlanName = policy => {
  const partnerGuid = getPartnerGuid()

  // For AccessBank, get plan from quotation
  if (partnerGuid === 'accessbank') {
    if (
      policy &&
      policy.quotation &&
      policy.quotation.additional_details &&
      policy.quotation.additional_details.primary_plan
    ) {
      const primaryPlan = policy.quotation.additional_details.primary_plan
      // Replace underscores with spaces
      return primaryPlan.replace(/_/g, ' ')
    }
  }

  // For other partners, use product_name
  return policy.product_name
}

const PolicyDetails = props => {
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [selectedPolicyAction, setSelectedPolicyAction] = React.useState(null)
  const [showReinstateDialog, setShowReinstateDialog] = React.useState(false)
  const [showCancellationDialog, setShowCancellationDialog] = React.useState(false)
  const [cancellationFormData, setCancellationFormData] = React.useState({
    reason: '',
    effective_date: '',
    notes: ''
  })

  const open = Boolean(anchorEl)

  const handleClick = (event, policy) => {
    setAnchorEl(event.currentTarget)
    setSelectedPolicyAction(policy)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }
  const handleCancellationFormDataChange = (field, value) => {
    setCancellationFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const classes = useStyle()

  const currencyCode = useProductCurrency(
    props.policy && props.policy.product_code
  )

  if (!props.policy) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12} md={12} className={classes.claimwrapper}>
          <Grid container spacing={2} className={classes.claimdetailswrapper}>
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              className={classes.ClaimDetailscontent}
            >
              <p style={{ textAlign: 'center' }}>No details found</p>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  if (props.loader) {
    return <SkeletonWrapper loading={props.loader} />
  }

  const partnerGuid = getPartnerGuid()
  if (partnerGuid === 'cover_link') {
    return (
      <CoverLinkPolicyDetails
        policy={props.policy}
        setQuotationToReCreate={props.setQuotationToReCreate}
        toggleUpdateBeneficaryDialog={props.toggleUpdateBeneficaryDialog}
        toggleOpenCancelPolicyDialog={props.toggleOpenCancelPolicyDialog}
        setPolicyToDownloadDocumentFor={props.setPolicyToDownloadDocumentFor}
      />
    )
  }

  if (partnerGuid === 'omu_flp' || partnerGuid === 'omk') {
    return (
      <PolicyDetailsFlp
        policy={props.policy}
        DocumentUpload={props.DocumentUpload}
        setQuotationToReCreate={props.setQuotationToReCreate}
        toggleUpdateBeneficaryDialog={props.toggleUpdateBeneficaryDialog}
        toggleOpenCancelPolicyDialog={props.toggleOpenCancelPolicyDialog}
        setPolicyToDownloadDocumentFor={props.setPolicyToDownloadDocumentFor}
      />
    )
  }

  return (
    <>
      <Grid container className={classes.policyWrapper}>
        <Grid item xs={12} sm={12} md={12}>
          <Grid container className={classes.policyHeader}>
            <Grid item xs={12} sm={6} md={6}>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  md={2}
                  sm={2}
                  className={classes.policyIconWrapper}
                >
                  <Box className={classes.policyIcons}>
                    <NoteAddOutlinedIcon
                      fontSize="14px"
                      sx={{ color: '#ffffff' }}
                    />
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={10}
                  sm={10}
                  className={classes.policyHeaderContent}
                >
                  <h6>{props.policy.policy_number || 'N/A'}</h6>
                  <p> Policy Number </p>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Grid container>
                <Grid item xs={12} md={6} sm={6}>
                  <div></div>
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={6}
                  sm={6}
                  className={classes.policyActiveStatusWrapper}
                >
                  <Box display="flex" justifyContent="flex-end">
                    <ul>
                      <li
                        className={
                          props.policy.active === true
                            ? classes.activePill
                            : classes.inactivePill
                        }
                      >
                        {' '}
                        <span>
                          {props.policy.active === true ? `Active` : `Inactive`}
                        </span>
                      </li>

                      {((PARTNERS_WITH_POLICY_NUMBER_SPECIAL_CHARACTORS.includes(
                        getPartnerGuid()
                      ) &&
                        hasAuthourity('POLICY_ACTIONS')) ||
                        !PARTNERS_WITH_POLICY_NUMBER_SPECIAL_CHARACTORS.includes(
                          getPartnerGuid()
                        )) && (
                        <li className={classes.actionbtnpolicy}>
                          <IconButton
                            aria-label="more"
                            id="long-button"
                            aria-controls={open ? 'long-menu' : undefined}
                            aria-expanded={open ? 'true' : undefined}
                            aria-haspopup="true"
                            onClick={event => handleClick(event, props.policy)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                          {selectedPolicyAction &&
                            selectedPolicyAction.guid === props.policy.guid && (
                              <Menu
                                id="long-menu"
                                MenuListProps={{
                                  'aria-labelledby': 'long-button'
                                }}
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                                PaperProps={{
                                  style: {
                                    maxHeight: ITEM_HEIGHT * 4.5,
                                    width: '20ch'
                                  }
                                }}
                              >
                                {canAmendPolicy(props.policy) &&
                                  !PARTNERS_WITH_POLICY_NUMBER_SPECIAL_CHARACTORS.includes(
                                    props.policy.partner
                                  ) &&
                                  partnerGuid !== PARTNERS_GUID_ENUM.SAMBA && (
                                    <MenuItem
                                      style={{
                                        cursor: 'pointer',
                                        fontFamily: 'Open Sans',
                                        fontStyle: 'normal',
                                        fontWeight: 400,
                                        fontSize: '12px',
                                        lineHeight: '15px',
                                        color: '#000000'
                                        //  display: hasAuthourity('CAPTURE_POLICY') ? 'block' : 'none'
                                      }}
                                      onClick={() => {
                                        props.setQuotationToReCreate(
                                          props.policy
                                        )
                                        setAnchorEl(null)
                                      }}
                                    >
                                      Amend
                                    </MenuItem>
                                  )}

                                {EMBEDDED_AMENDMENT_PARTNERS.includes(
                                  partnerGuid
                                ) &&
                                  canAmendPolicy(props.policy) && (
                                    <MenuItem
                                      style={{
                                        cursor: 'pointer',
                                        fontFamily: 'Open Sans',
                                        fontStyle: 'normal',
                                        fontWeight: 400,
                                        fontSize: '12px',
                                        lineHeight: '15px',
                                        color: '#000000'
                                      }}
                                      onClick={() => {
                                        setAnchorEl(null)
                                        hashHistory.push({
                                          pathname: '/admin/amend-policy',
                                          query: {
                                            policy_number:
                                              props.policy.policy_number
                                          }
                                        })
                                      }}
                                    >
                                      Amend Policy
                                    </MenuItem>
                                  )}

                                {!['Discarded', 'Cancelled'].includes(
                                  props.policy.status
                                ) && (
                                  <MenuItem
                                    style={{
                                      cursor: 'pointer',
                                      fontFamily: 'Open Sans',
                                      fontStyle: 'normal',
                                      fontWeight: 400,
                                      fontSize: '12px',
                                      lineHeight: '15px',
                                      color: '#000000'
                                    }}
                                    onClick={() => {
                                      props.toggleUpdateBeneficaryDialog(
                                        props.policy
                                      )
                                      setAnchorEl(null)
                                    }}
                                  >
                                    Update Beneficiary
                                  </MenuItem>
                                )}

                                {!['Cancelled', 'Rejected', 'Non_Payment', 'Not_Taken_Up'].includes(
                                  props.policy.status
                                ) && (
                                  <MenuItem
                                    style={{
                                      cursor: 'pointer',
                                      fontFamily: 'Open Sans',
                                      fontStyle: 'normal',
                                      fontWeight: 400,
                                      fontSize: '12px',
                                      lineHeight: '15px',
                                      color: '#000000'
                                    }}
                                    onClick={() => {
                                      setShowCancellationDialog(true)
                                      setCancellationFormData({
                                        reason: '',
                                        effective_date: '',
                                        notes: ''
                                      })
                                      setAnchorEl(null)
                                    }}
                                  >
                                    Request Cancellation
                                  </MenuItem>
                                )}

                                <MenuItem
                                  style={{
                                    cursor: 'pointer',
                                    fontFamily: 'Open Sans',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: '12px',
                                    lineHeight: '15px',
                                    color: '#000000'
                                  }}
                                  onClick={() => {
                                    props.setPaymentInitiation(props.policy)
                                    setAnchorEl(null)
                                  }}
                                >
                                  Initiate Payment{' '}
                                </MenuItem>

                                {![
                                  'Non_Payment',
                                  'Cancelled',
                                  'Rejected',
                                  'Un_Confirmed'
                                ].includes(props.policy.status) &&
                                  partnerGuid !== 'old_mutual_malawi' && (
                                  <MenuItem
                                    style={{
                                      cursor: 'pointer',
                                      fontFamily: 'Open Sans',
                                      fontStyle: 'normal',
                                      fontWeight: 400,
                                      fontSize: '12px',
                                      lineHeight: '15px',
                                      color: '#000000',
                                      display: hasAuthourity('CAPTURE_POLICY')
                                        ? 'block'
                                        : 'none'
                                    }}
                                    onClick={() => {
                                      setAnchorEl(null)
                                      props.toggleOpenCancelPolicyDialog()
                                    }}
                                  >
                                    Cancel Policy
                                  </MenuItem>
                                )}

                                {REINSTATEMENT_ALLOWED_PARTNERS.includes(
                                  partnerGuid
                                ) &&
                                  REINSTATEMENT_ALLOWED_STATUSES.includes(
                                    props.policy.status
                                  ) && (
                                    <MenuItem
                                      style={{
                                        cursor: 'pointer',
                                        fontFamily: 'Open Sans',
                                        fontStyle: 'normal',
                                        fontWeight: 400,
                                        fontSize: '12px',
                                        lineHeight: '15px',
                                        color: '#000000',
                                        display: hasAuthourity('CAPTURE_POLICY')
                                          ? 'block'
                                          : 'none'
                                      }}
                                      onClick={() => {
                                        setAnchorEl(null)
                                        setShowReinstateDialog(true)
                                      }}
                                    >
                                      Reinstate Policy
                                    </MenuItem>
                                  )}

                                {![
                                  'Non_Payment',
                                  'Cancelled',
                                  'Rejected',
                                  'Un_Confirmed',
                                  'Unpaid'
                                ].includes(props.policy.status) && (
                                  <MenuItem
                                    style={{
                                      cursor: 'pointer',
                                      fontFamily: 'Open Sans',
                                      fontStyle: 'normal',
                                      fontWeight: 400,
                                      fontSize: '12px',
                                      lineHeight: '15px',
                                      color: '#000000'
                                    }}
                                    onClick={() => {
                                      setAnchorEl(null)
                                      props.setPolicyToDownloadDocumentFor(
                                        props.policy
                                      )
                                    }}
                                  >
                                    Download Policy Document
                                  </MenuItem>
                                )}

                                <MenuItem
                                  style={{
                                    cursor: 'pointer',
                                    fontFamily: 'Open Sans',
                                    fontStyle: 'normal',
                                    fontWeight: 400,
                                    fontSize: '12px',
                                    lineHeight: '15px',
                                    color: '#000000'
                                  }}
                                  onClick={() => {
                                    setAnchorEl(null)
                                    props.setPolicyToEmailDocumentFor(
                                      props.policy
                                    )
                                  }}
                                >
                                  Email Document
                                </MenuItem>

                                {TRANSFER_POLICY_ALLOWED_PARTNERS.includes(
                                  partnerGuid
                                ) && (
                                  <MenuItem
                                    style={{
                                      cursor: 'pointer',
                                      fontFamily: 'Open Sans',
                                      fontStyle: 'normal',
                                      fontWeight: 400,
                                      fontSize: '12px',
                                      lineHeight: '15px',
                                      color: '#000000'
                                    }}
                                    onClick={() => {
                                      setAnchorEl(null)
                                      props.toggleTransferPolicyDialog()
                                    }}
                                  >
                                    Transfer Policy
                                  </MenuItem>
                                )}
                              </Menu>
                            )}
                        </li>
                      )}
                    </ul>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <div className={classes.policyHeaderBorder}></div>
          </Grid>

          <Grid container spacing={2} className={classes.policyContentWrapper}>
            <Grid item xs={12} sm={3} md={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3} md={3}></Grid>
                <Grid item xs={12} sm={9} md={9}>
                  <p>Plan </p>
                  <h6>
                    {' '}
                    {getPlanName(props.policy)}{' '}
                    {props.policy.product_name === 'Family Clan Cover' &&
                      ` ( P${props.policy.product_code} )`}
                  </h6>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={3} md={3}>
              <p> Start Date</p>
              <h6>{formatDateLocale(props.policy.start_date) || 'N/A'}</h6>
            </Grid>
            <Grid item xs={12} sm={3} md={3}>
              <p>Due Date</p>
              <h6>{formatDateLocale(props.policy.due_date) || 'N/A'}</h6>
            </Grid>
            <Grid item xs={12} sm={3} md={3}>
              <p>Premiums</p>
              <h6>
                {props.policy.quotation &&
                typeof props.policy.quotation.total_premium_amount_in_cents ===
                  'number'
                  ? `${currencyCode} ${(
                      props.policy.quotation.total_premium_amount_in_cents / 100
                    ).toFixed(2)} per ${
                      props.policy.quotation.premium_cardinality
                    } ${props.policy.quotation.premium_granularity}`
                  : 'N/A'}
              </h6>
            </Grid>
          </Grid>
          <div className={classes.policyContentBorder}></div>
          <Grid container spacing={2} className={classes.policyFooterWrapper}>
            <Grid item xs={12} md={6} sm={6}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6} sm={6}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3} md={3}></Grid>
                    <Grid item xs={12} sm={9} md={9}>
                      <p>Beneficiary Name</p>
                      <h6>
                        {(props.policy.beneficiaries &&
                          props.policy.beneficiaries.length > 0 &&
                          props.policy.beneficiaries[0].full_name) ||
                          props.policy.beneficiary_name ||
                          'N/A'}
                      </h6>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={6} sm={6}>
                  <p> Beneficiary Phone Number</p>
                  <h6>
                    {(props.policy.beneficiaries &&
                      props.policy.beneficiaries.length > 0 &&
                      props.policy.beneficiaries[0].msisdn) ||
                      props.policy.beneficiary_msisdn ||
                      'N/A'}
                  </h6>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}></Grid>
                <Grid container xs={12} sm={6} md={6}>
                  {props.policy.additional_details ? (
                    <>
                      <Grid item xs={12} sm={4} md={4}>
                        <div className={classes.policyBalance}>
                          <p
                            style={{
                              textAlign: 'center'
                            }}
                          >
                            Branch
                          </p>
                          <h6
                            style={{
                              textAlign: 'center'
                            }}
                          >
                            {props.policy.additional_details.branch || 'N/A'}
                          </h6>
                        </div>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12} sm={4} md={4}></Grid>
                    </>
                  )}

                  <Grid item xs={12} sm={4} md={4}>
                    <div className={classes.policyBalance}>
                      <p
                        style={{
                          textAlign: 'center'
                        }}
                      >
                        status
                      </p>
                      <h6
                        style={{
                          textAlign: 'center'
                        }}
                      >
                        {partnerGuid === 'old_mutual_malawi'
                          ? getOmMalawiDisplayStatus(props.policy)
                          : props.policy.status || 'N/A'}
                      </h6>
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={4} md={4}>
                    <div className={classes.policyBalance}>
                      <p>Balance</p>
                      <h6
                        style={
                          (partnerGuid === 'old_mutual_malawi' ||
                            partnerGuid === 'samba') &&
                          props.policy.granularity_balance_amount_in_cents <= 0
                            ? { color: 'red' }
                            : { color: 'green' }
                        }
                      >
                        {partnerGuid === 'old_mutual_malawi' ||
                        partnerGuid === 'samba'
                          ? `${currencyCode} ${(
                              props.policy.granularity_balance_amount_in_cents /
                              100
                            ).toFixed(2)}`
                          : `${currencyCode} ${(
                              props.policy.balance_amount_in_cents / 100
                            ).toFixed(2)}`}
                      </h6>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            {partnerGuid === 'old_mutual_malawi' && (
              <Grid item xs={12} md={12} sm={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12} sm={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={1} md={1}></Grid>
                      <Grid item xs={12} sm={11} md={11}>
                        {props.policy.additional_details ? (
                          (() => {
                            const { additional_details } = props.policy
                            const paymentMethod =
                              props.policy.payment_method ||
                              additional_details.payment_method
                            if (
                              (paymentMethod === 'Mobile Money' ||
                                paymentMethod === 'Mobile_Money') &&
                              (additional_details.mobile_money ||
                                (additional_details.payment_info &&
                                  additional_details.payment_info.mobile_money))
                            ) {
                              return (
                                <div>
                                  <p
                                    style={{
                                      fontWeight: 'bold',
                                      marginBottom: '10px'
                                    }}
                                  >
                                    Payment Info - Mobile Money
                                  </p>
                                  <div style={{ marginTop: '10px' }}>
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'start',
                                        alignItems: 'center',
                                        marginBottom: '5px',
                                        gap: '10px'
                                      }}
                                    >
                                      <p
                                        style={{
                                          margin: 0,
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        Provider:
                                      </p>
                                      <h6 style={{ margin: 0 }}>
                                        {(additional_details.mobile_money &&
                                          additional_details.mobile_money
                                            .provider) ||
                                          (additional_details.payment_info &&
                                            additional_details.payment_info
                                              .mobile_money &&
                                            additional_details.payment_info
                                              .mobile_money.provider) ||
                                          'N/A'}
                                      </h6>
                                    </div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'start',
                                        alignItems: 'center',
                                        marginBottom: '5px',
                                        gap: '10px'
                                      }}
                                    >
                                      <p
                                        style={{
                                          margin: 0,
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        Phone Number:
                                      </p>
                                      <h6 style={{ margin: 0 }}>
                                        {(additional_details.mobile_money &&
                                          additional_details.mobile_money
                                            .phone_number) ||
                                          (additional_details.payment_info &&
                                            additional_details.payment_info
                                              .mobile_money &&
                                            additional_details.payment_info
                                              .mobile_money.phone_number) ||
                                          'N/A'}
                                      </h6>
                                    </div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'start',
                                        alignItems: 'center',
                                        marginBottom: '5px',
                                        gap: '10px'
                                      }}
                                    >
                                      <p
                                        style={{
                                          margin: 0,
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        Account Holder:
                                      </p>
                                      <h6 style={{ margin: 0 }}>
                                        {(additional_details.mobile_money &&
                                          additional_details.mobile_money
                                            .account_holder_name) ||
                                          (additional_details.payment_info &&
                                            additional_details.payment_info
                                              .mobile_money &&
                                            additional_details.payment_info
                                              .mobile_money
                                              .account_holder_name) ||
                                          additional_details.payer_full_name ||
                                          'N/A'}
                                      </h6>
                                    </div>
                                  </div>
                                </div>
                              )
                            }
                            if (additional_details.bank_account) {
                              return (
                                <div>
                                  <p
                                    style={{
                                      fontWeight: 'bold',
                                      marginBottom: '10px'
                                    }}
                                  >
                                    Payment Info - Bank Account
                                  </p>
                                  <div style={{ marginTop: '10px' }}>
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'start',
                                        alignItems: 'center',
                                        marginBottom: '5px',
                                        gap: '10px'
                                      }}
                                    >
                                      <p
                                        style={{
                                          margin: 0,
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        Account Number:
                                      </p>
                                      <h6 style={{ margin: 0 }}>
                                        {additional_details.bank_account
                                          .account_number || 'N/A'}
                                      </h6>
                                    </div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'start',
                                        alignItems: 'center',
                                        marginBottom: '5px',
                                        gap: '10px'
                                      }}
                                    >
                                      <p
                                        style={{
                                          margin: 0,
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        Bank Name:
                                      </p>
                                      <h6 style={{ margin: 0 }}>
                                        {additional_details.bank_account
                                          .bank_name || 'N/A'}
                                      </h6>
                                    </div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'start',
                                        alignItems: 'center',
                                        marginBottom: '5px',
                                        gap: '10px'
                                      }}
                                    >
                                      <p
                                        style={{
                                          margin: 0,
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        Branch Name:
                                      </p>
                                      <h6 style={{ margin: 0 }}>
                                        {additional_details.bank_account
                                          .branch_name || 'N/A'}
                                      </h6>
                                    </div>
                                  </div>
                                </div>
                              )
                            }
                            if (additional_details.stop_order) {
                              return (
                                <div>
                                  <p
                                    style={{
                                      fontWeight: 'bold',
                                      marginBottom: '10px'
                                    }}
                                  >
                                    Payment Info - Stop Order
                                  </p>
                                  <div style={{ marginTop: '10px' }}>
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'start',
                                        alignItems: 'center',
                                        marginBottom: '5px',
                                        gap: '10px'
                                      }}
                                    >
                                      <p
                                        style={{
                                          margin: 0,
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        Employer Name:
                                      </p>
                                      <h6 style={{ margin: 0 }}>
                                        {additional_details.stop_order
                                          .employer_name || 'N/A'}
                                      </h6>
                                    </div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'start',
                                        alignItems: 'center',
                                        marginBottom: '5px',
                                        gap: '10px'
                                      }}
                                    >
                                      <p
                                        style={{
                                          margin: 0,
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        Employee Number:
                                      </p>
                                      <h6 style={{ margin: 0 }}>
                                        {additional_details.stop_order
                                          .employee_number || 'N/A'}
                                      </h6>
                                    </div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'start',
                                        alignItems: 'center',
                                        marginBottom: '5px',
                                        gap: '10px'
                                      }}
                                    >
                                      <p
                                        style={{
                                          margin: 0,
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        Position:
                                      </p>
                                      <h6 style={{ margin: 0 }}>
                                        {additional_details.stop_order
                                          .position || 'N/A'}
                                      </h6>
                                    </div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'start',
                                        alignItems: 'center',
                                        marginBottom: '5px',
                                        gap: '10px'
                                      }}
                                    >
                                      <p
                                        style={{
                                          margin: 0,
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        Occupation:
                                      </p>
                                      <h6 style={{ margin: 0 }}>
                                        {additional_details.stop_order
                                          .occupation || 'N/A'}
                                      </h6>
                                    </div>
                                  </div>
                                </div>
                              )
                            }
                            if (additional_details.mobile_money) {
                              return (
                                <div>
                                  <p
                                    style={{
                                      fontWeight: 'bold',
                                      marginBottom: '10px'
                                    }}
                                  >
                                    Payment Info - Mobile Money
                                  </p>
                                  <div style={{ marginTop: '10px' }}>
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'start',
                                        alignItems: 'center',
                                        marginBottom: '5px',
                                        gap: '10px'
                                      }}
                                    >
                                      <p
                                        style={{
                                          margin: 0,
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        Provider:
                                      </p>
                                      <h6 style={{ margin: 0 }}>
                                        {additional_details.mobile_money
                                          .provider || 'N/A'}
                                      </h6>
                                    </div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'start',
                                        alignItems: 'center',
                                        marginBottom: '5px',
                                        gap: '10px'
                                      }}
                                    >
                                      <p
                                        style={{
                                          margin: 0,
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        Phone Number:
                                      </p>
                                      <h6 style={{ margin: 0 }}>
                                        {additional_details.mobile_money
                                          .phone_number || 'N/A'}
                                      </h6>
                                    </div>
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'start',
                                        alignItems: 'center',
                                        marginBottom: '5px',
                                        gap: '10px'
                                      }}
                                    >
                                      <p
                                        style={{
                                          margin: 0,
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        Account Holder:
                                      </p>
                                      <h6 style={{ margin: 0 }}>
                                        {additional_details.mobile_money
                                          .account_holder_name || 'N/A'}
                                      </h6>
                                    </div>
                                  </div>
                                </div>
                              )
                            }
                            return (
                              <div>
                                <p
                                  style={{
                                    fontWeight: 'bold',
                                    marginBottom: '10px'
                                  }}
                                >
                                  Payment Info
                                </p>
                                <p>N/A</p>
                              </div>
                            )
                          })()
                        ) : (
                          <div>
                            <p
                              style={{
                                fontWeight: 'bold',
                                marginBottom: '10px'
                              }}
                            >
                              Payment Info
                            </p>
                            <p>N/A</p>
                          </div>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>

      <ReinstatePolicy
        open={showReinstateDialog}
        onClose={() => setShowReinstateDialog(false)}
        policy={props.policy}
        onSuccess={() => {
          setTimeout(() => {
            window.location.reload()
          }, 1500)
        }}
      />

      <CancellationRequestDialog
        open={showCancellationDialog}
        onClose={() => {
          setShowCancellationDialog(false)
          setCancellationFormData({
            reason: '',
            effective_date: '',
            notes: ''
          })
        }}
        policy={props.policy}
        formData={cancellationFormData}
        onFormDataChange={handleCancellationFormDataChange}
      />
    </>
  )
}

export default PolicyDetails
